// b16: värmarens signal "Temperaturen vid utblåset" (BK40, provas och omvärderas). Ägaren väljer
// givaren vid utblåset (en givare över Bluetooth, eller en temperatur i Home Assistant), en
// jämförelse (kabinen, inne eller ute) och gränserna. Kurvan visar utblåset och jämförelsen de
// senaste 24 timmarna, och när värmaren räknades som igång, så att ägaren kan se om det stämmer.
//
// Samma delar under Inställningar → Båten → Förbrukarna och, för ett hus, under Förbrukarna (till
// exempel en luftvärmepump eller en kamin).

import {
  FORBRUKARE,
  type Forbrukarnyckel,
  givarnamn,
  type Platsinstallningar,
  type Platstyp,
  tal,
  type Uppstallningsforbrukare,
  UTBLAS_FORVAL,
  type Utblas,
  type Utblaskurva,
} from "@batkoll/gemensam";
import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { api, Felsvar } from "../api.ts";
import { Kort } from "../komponenter.tsx";

export interface Utblasutkast {
  /** Givarens id i navet, eller "ha" med entiteten. */
  givare: string;
  entitet: string;
  jamforelse: string;
  paGrader: string;
  paMinuter: string;
  avGrader: string;
  avMinuter: string;
}

const visa = (v: number) => String(v).replace(".", ",");

/** Jämförelserna som platsen kan ha, i ord. */
export function jamforelserFor(typ: Platstyp): { id: string; namn: string }[] {
  return typ === "bat"
    ? [
        { id: "kabin.temp", namn: "Temperaturen i kabinen" },
        { id: "ute.temp", namn: "Temperaturen ute" },
        { id: "motorrum.temp", namn: "Temperaturen i motorrummet" },
      ]
    : [
        { id: "hus.inne.temp", namn: "Temperaturen inne" },
        { id: "hus.ute.temp", namn: "Temperaturen ute (huset)" },
        { id: "ute.temp", namn: "Temperaturen ute (väderstationen)" },
      ];
}

/** Utkastet för utblåset, med förvalen. */
export function utblasUtkast(u: Utblas | null | undefined, typ: Platstyp): Utblasutkast {
  return {
    givare: u ? (u.givare.startsWith("ha:") ? "ha" : u.givare) : "",
    entitet: u?.givare.startsWith("ha:") ? u.givare.slice(3) : "",
    jamforelse: u?.jamforelse ?? (jamforelserFor(typ)[0]?.id as string),
    paGrader: visa(u?.paGrader ?? UTBLAS_FORVAL.paGrader),
    paMinuter: visa(u?.paMinuter ?? UTBLAS_FORVAL.paMinuter),
    avGrader: visa(u?.avGrader ?? UTBLAS_FORVAL.avGrader),
    avMinuter: visa(u?.avMinuter ?? UTBLAS_FORVAL.avMinuter),
  };
}

/** Utblåset ur utkastet. Kastar Felsvar för det som inte är ifyllt. */
export function utblasFran(d: Utblasutkast): Utblas {
  const t = (s: string, namn: string) => {
    const v = Number(s.trim().replace(",", "."));
    if (!s.trim() || !Number.isFinite(v)) throw new Felsvar(`${namn} ska vara ett tal.`, 400);
    return v;
  };
  if (d.givare === "") throw new Felsvar("Välj givaren vid utblåset.", 400);
  const entitet = d.entitet.trim();
  if (d.givare === "ha" && !/^sensor\.[a-z0-9_]+$/.test(entitet)) {
    throw new Felsvar(
      "Skriv temperaturens entitet i Home Assistant, till exempel sensor.kaminen.",
      400,
    );
  }
  return {
    givare: d.givare === "ha" ? `ha:${entitet}` : d.givare,
    jamforelse: d.jamforelse,
    paGrader: t(d.paGrader, "Gränsen för på"),
    paMinuter: t(d.paMinuter, "Minuterna för på"),
    avGrader: t(d.avGrader, "Gränsen för av"),
    avMinuter: t(d.avMinuter, "Minuterna för av"),
  };
}

/** Fälten för utblåset: givaren, jämförelsen och gränserna. */
export function Utblasfalt(props: {
  namn: string;
  d: Utblasutkast;
  satt: (d: Utblasutkast) => void;
  s: Platsinstallningar;
}) {
  const { d, s } = props;
  const lista = s.givare?.lista ?? [];
  const andra = (x: Partial<Utblasutkast>) => props.satt({ ...d, ...x });
  const harGivaren = d.givare === "" || d.givare === "ha" || lista.some((g) => g.id === d.givare);
  const tal_ = (namn: string, v: string, f: keyof Utblasutkast) => (
    <label>
      {namn}
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={v}
        onInput={(e) => andra({ [f]: e.currentTarget.value })}
      />
    </label>
  );
  return (
    <>
      <label>
        {props.namn}, givaren vid utblåset
        <select value={d.givare} onChange={(e) => andra({ givare: e.currentTarget.value })}>
          <option value="">Välj givaren</option>
          {!harGivaren && <option value={d.givare}>{givarnamn(null, d.givare)}</option>}
          {lista.map((g) => (
            <option key={g.id} value={g.id}>
              {givarnamn(g.modell, g.id)}
              {g.temp !== null ? `, ${tal(g.temp, 1)} °C nu` : ""}
            </option>
          ))}
          <option value="ha">En temperatur i Home Assistant</option>
        </select>
      </label>
      {d.givare === "ha" && (
        <label>
          {props.namn}, temperaturens entitet i Home Assistant
          <input
            type="text"
            autoComplete="off"
            placeholder="sensor.kaminen"
            value={d.entitet}
            onInput={(e) => andra({ entitet: e.currentTarget.value })}
          />
        </label>
      )}
      <label>
        {props.namn}, jämförelsen
        <select value={d.jamforelse} onChange={(e) => andra({ jamforelse: e.currentTarget.value })}>
          {jamforelserFor(s.plats.typ).map((j) => (
            <option key={j.id} value={j.id}>
              {j.namn}
            </option>
          ))}
        </select>
      </label>
      <div class="faltrad">
        {tal_("På vid °C varmare", d.paGrader, "paGrader")}
        {tal_("i minuter", d.paMinuter, "paMinuter")}
        {tal_("Av under °C varmare", d.avGrader, "avGrader")}
        {tal_("i minuter", d.avMinuter, "avMinuter")}
      </div>
      <p class="liten">
        {props.namn} räknas som igång när utblåset har varit minst {d.paGrader || "–"} °C varmare än
        jämförelsen i {d.paMinuter || "–"} minuter, och som avslagen när skillnaden har varit under{" "}
        {d.avGrader || "–"} °C i {d.avMinuter || "–"} minuter. Det provas: se kurvan.
      </p>
    </>
  );
}

const TIMME = 3_600_000;

/** Kurvan: utblåset, jämförelsen och när värmaren räknades som igång, de senaste 24 timmarna. */
export function Utblaskurvan(props: { platsId: string; nyckel: Forbrukarnyckel; nu: number }) {
  const [k, setK] = useState<Utblaskurva | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  useEffect(() => {
    api
      .utblaskurva(props.platsId, props.nyckel)
      .then(setK)
      .catch(() => setFel("Kurvan går inte att hämta nu."));
  }, [props.platsId, props.nyckel]);
  if (fel) return <p class="liten">{fel}</p>;
  if (!k) return <p class="liten">Hämtar kurvan …</p>;
  const b = 360;
  const h = 140;
  const fran = props.nu - 24 * TIMME;
  const alla = [...k.utblas, ...k.jamfort].map((p) => p.varde);
  if (k.utblas.length === 0) {
    return (
      <p class="liten">
        Ingen temperatur vid utblåset de senaste 24 timmarna än. Kurvan kommer när navet har hört
        givaren.
      </p>
    );
  }
  const min = Math.floor(Math.min(...alla) - 2);
  const max = Math.ceil(Math.max(...alla) + 2);
  const x = (t: number) => Math.max(0, Math.min(b, ((t - fran) / (24 * TIMME)) * b));
  const y = (v: number) => h - ((v - min) / Math.max(1, max - min)) * h;
  const linje = (p: { tid: number; varde: number }[]) =>
    p.map((q) => `${x(q.tid).toFixed(1)},${y(q.varde).toFixed(1)}`).join(" ");
  const timmar = k.igang.reduce(
    (s, i) => s + ((i.till ?? props.nu) - Math.max(i.fran, fran)) / TIMME,
    0,
  );
  return (
    <figure>
      <svg
        class="utblaskurva"
        viewBox={`0 0 ${b} ${h + 14}`}
        role="img"
        aria-label={`${k.namn}: utblåset och jämförelsen de senaste 24 timmarna, igång ${tal(timmar, 1)} timmar`}
      >
        {k.igang.map((i) => (
          <rect
            key={i.fran}
            class="igang"
            x={x(i.fran)}
            y={0}
            width={Math.max(1, x(i.till ?? props.nu) - x(i.fran))}
            height={h}
          />
        ))}
        <polyline class="jamfort" points={linje(k.jamfort)} />
        <polyline class="utblas" points={linje(k.utblas)} />
        <text class="axel" x={2} y={10}>
          {max} °C
        </text>
        <text class="axel" x={2} y={h - 2}>
          {min} °C
        </text>
        <text class="axel" x={2} y={h + 12}>
          för 24 h sedan
        </text>
        <text class="axel" x={b - 22} y={h + 12}>
          nu
        </text>
      </svg>
      <figcaption class="liten">
        Utblåset (orange) och jämförelsen (blå). Skuggat: värmaren räknades som igång,{" "}
        {tal(timmar, 1)} timmar de senaste 24 timmarna.
      </figcaption>
    </figure>
  );
}

/** b16: förbrukarna i ett hus, till exempel en luftvärmepump eller en kamin. */
export function Husforbrukardel(props: {
  s: Platsinstallningar;
  spara: (
    sektion: string,
    gor: () => Promise<Platsinstallningar | [Platsinstallningar, string]>,
    ok?: string,
  ) => Promise<boolean>;
  beskedrad: ComponentChildren;
}) {
  const { s } = props;
  const sparade = s.forbrukare ?? null;
  const [d, setD] = useState(() =>
    (sparade ?? []).map((f) => ({
      nyckel: f.nyckel,
      namn: f.namn,
      signal: f.utblas ? "utblas" : f.signal?.startsWith("ha:") ? "ha" : "",
      entitet: f.signal?.startsWith("ha:") ? f.signal.slice(3) : "",
      utblas: utblasUtkast(f.utblas, s.plats.typ),
    })),
  );
  if (sparade === null) return null;
  const fria = FORBRUKARE.filter((n) => !d.some((x) => x.nyckel === n));
  const andra = (n: string, x: Partial<(typeof d)[number]>) =>
    setD(d.map((y) => (y.nyckel === n ? { ...y, ...x } : y)));

  function lista(): Uppstallningsforbrukare[] {
    return d.map((f) => {
      const entitet = f.entitet.trim();
      if (f.signal === "ha" && !/^[a-z_]+\.[a-z0-9_]+$/.test(entitet)) {
        throw new Felsvar("Skriv entiteten i Home Assistant, till exempel switch.kaminen.", 400);
      }
      return {
        nyckel: f.nyckel,
        namn: f.namn,
        lh: null,
        tank: null,
        signal: f.signal === "ha" ? `ha:${entitet}` : null,
        utblas: f.signal === "utblas" ? utblasFran(f.utblas) : null,
      };
    });
  }

  return (
    <Kort rubrik="Förbrukarna" bred>
      <p class="liten">
        Till exempel en luftvärmepump eller en kamin, och när den går: en entitet i Home Assistant,
        eller temperaturen vid utblåset.
      </p>
      <form
        class="formular"
        aria-label="Förbrukarna i huset"
        onSubmit={(e) => {
          e.preventDefault();
          void props.spara("husforbrukare", async () =>
            api.andraPlats(s.plats.id, { forbrukare: lista() }),
          );
        }}
      >
        {d.map((f) => (
          <div key={f.nyckel} class="uppstallning-rad">
            <label>
              Namnet
              <input
                type="text"
                maxLength={40}
                value={f.namn}
                onInput={(e) => andra(f.nyckel, { namn: e.currentTarget.value })}
              />
            </label>
            <label>
              {f.namn || "Förbrukaren"}, signalen för när den går
              <select
                value={f.signal}
                onChange={(e) => andra(f.nyckel, { signal: e.currentTarget.value })}
              >
                <option value="">Ingen signal</option>
                <option value="ha">En entitet i Home Assistant</option>
                <option value="utblas">Temperaturen vid utblåset</option>
              </select>
            </label>
            {f.signal === "ha" && (
              <label>
                {f.namn || "Förbrukaren"}, entiteten i Home Assistant
                <input
                  type="text"
                  autoComplete="off"
                  value={f.entitet}
                  onInput={(e) => andra(f.nyckel, { entitet: e.currentTarget.value })}
                />
              </label>
            )}
            {f.signal === "utblas" && (
              <Utblasfalt
                namn={f.namn || "Förbrukaren"}
                d={f.utblas}
                satt={(u) => andra(f.nyckel, { utblas: u })}
                s={s}
              />
            )}
            {sparade.find((x) => x.nyckel === f.nyckel)?.utblas && (
              <Utblaskurvan platsId={s.plats.id} nyckel={f.nyckel} nu={Date.now()} />
            )}
            <button
              type="button"
              class="knapp-lank"
              onClick={() => setD(d.filter((y) => y.nyckel !== f.nyckel))}
            >
              Ta bort {f.namn || "förbrukaren"}
            </button>
          </div>
        ))}
        <div class="knapprad">
          {fria.length > 0 && (
            <button
              type="button"
              class="knapp-andra"
              onClick={() =>
                setD([
                  ...d,
                  {
                    nyckel: fria[0] as Forbrukarnyckel,
                    namn: d.length === 0 ? "Kaminen" : `Förbrukare ${d.length + 1}`,
                    signal: "utblas",
                    entitet: "",
                    utblas: utblasUtkast(null, s.plats.typ),
                  },
                ])
              }
            >
              Lägg till en förbrukare
            </button>
          )}
          <button type="submit" class="knapp-huvud">
            Spara förbrukarna
          </button>
        </div>
      </form>
      {props.beskedrad}
    </Kort>
  );
}
